import { axiosInstance } from './axiosConfig';
import { BASE_URL, STRIDE_AUTH_URL } from '../utility/constants';

/**
 * OIDC Authentication Service for handling OpenID Connect authentication
 * This service manages authentication without storing tokens in localStorage
 */
class OIDCAuthService {
  /**
   * Initiate OIDC login flow
   * @param {string} intendedRole - Intended user role for registration context
   * @param {string} clientId - Client ID from URL parameters (optional)
   * @param {string} instituteId - Institute ID from URL parameters (optional)
   * @returns {Promise<string>} - Authorization URL
   */
  static async initiateLogin(intendedRole = null, clientId = null, instituteId = null) {
    try {
      const state = this.generateState();
      const nonce = this.generateNonce();
      
      const params = {
        client_id: process.env.REACT_APP_OIDC_CLIENT_ID,
        redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI,
        next: `${window.location.protocol}//${window.location.host}/callback`,
        response_type: 'code',
        scope: 'openid profile email',
        state,
        nonce,
      };

      // Add intended role to state if provided
      if (intendedRole) {
        params.intended_role = intendedRole;
      }

      // Add client_id and institute_id if provided (for student/client auth flows)
      // Note: These are application-level client_id and institute_id, not the OIDC client_id
      if (clientId) {
        params.app_client_id = clientId;
      }
      if (instituteId) {
        params.institute_id = instituteId;
      }
      
      // Build the authorization URL with query parameters
      const queryString = new URLSearchParams(params).toString();
      const authorizationUrl = `${STRIDE_AUTH_URL}authorize?${queryString}`;
      
      // Directly redirect to the authorization URL (handles 301 redirects)
      window.location.href = authorizationUrl;
    } catch (error) {
      console.error('OIDC login initiation failed:', error);
      throw new Error('Failed to initiate OIDC login');
    }
  }

  /**
   * Initiate OIDC registration flow
   * @param {string} intendedRole - Intended user role for registration context
   * @param {string} clientId - Client ID from URL parameters (optional)
   * @param {string} instituteId - Institute ID from URL parameters (optional)
   * @returns {Promise<string>} - Authorization URL
   */
  static async initiateRegister(intendedRole = null, clientId = null, instituteId = null) {
    try {
      const state = this.generateState();
      const nonce = this.generateNonce();
      
      const params = {
        client_id: process.env.REACT_APP_OIDC_CLIENT_ID,
        redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI,
        response_type: 'code',
        next: `${window.location.protocol}//${window.location.host}/callback`,
        scope: 'openid profile email',
        state,
        nonce,
        login: false,
      };

      // Add intended role to state if provided
      if (intendedRole) {
        params.intended_role = intendedRole;
      }

      // Add client_id and institute_id if provided (for student/client auth flows)
      // Note: These are application-level client_id and institute_id, not the OIDC client_id
      if (clientId) {
        params.app_client_id = clientId;
      }
      if (instituteId) {
        params.institute_id = instituteId;
      }
      
      // Build the authorization URL with query parameters
      const queryString = new URLSearchParams(params).toString();
      const authorizationUrl = `${STRIDE_AUTH_URL}authorize?${queryString}`;
      
      // Directly redirect to the authorization URL (handles 301 redirects)
      console.log('authorizationUrl', authorizationUrl);
      window.location.href = authorizationUrl;
    } catch (error) {
      console.error('OIDC registration initiation failed:', error);
      throw new Error('Failed to initiate OIDC registration');
    }
  }

  /**
   * Validate current session
   * @returns {Promise<Object>} - User data if session is valid
   */
    static async validateSession() {
    try {
      // Check if we have tokens in session storage
      const accessToken = sessionStorage.getItem('access_token');
      const tokenExpiry = sessionStorage.getItem('token_expiry');
      
      if (!accessToken) {
        console.log('No access token found in session storage');
        return null;
      }
      
      // Check if token has expired
      if (tokenExpiry && Date.now() > parseInt(tokenExpiry)) {
        console.log('Token has expired, clearing session storage');
        this.clearAuthData();
        return null;
      }
      
      console.log('Validating session with stored token:', `${BASE_URL}v1/auth/me`);
      const response = await axiosInstance.get(`${BASE_URL}v1/auth/me`);
      console.log('Session validation successful:', response.data);

      return response.data;
    } catch (error) {
      console.log('Session validation error:', error.response?.status, error.message);
      if (error.response?.status === 401) {
        // Session is invalid, clear stored tokens
        console.log('401 received - clearing stored tokens');
        this.clearAuthData();
        return null;
      }
      console.error('Session validation failed:', error);
      return null;
    }
  }

  /**
   * Logout user and clear session
   * @param {string} userRole - User's role to determine redirect destination
   * @param {string} clientId - Client ID from Redux store
   * @param {string} instituteId - Institute ID from Redux store
   * @returns {Promise<void>}
   */
  static async logout(userRole = null, clientId = null, instituteId = null) {
    try {
      // Try to call OIDC logout endpoint
      console.log('Calling OIDC logout endpoint...');
      await axiosInstance.get(`${STRIDE_AUTH_URL}oidc/logout`);
      console.log('OIDC logout endpoint called successfully');
    } catch (logoutError) {
      console.warn('OIDC logout endpoint call failed:', logoutError);
      // Continue with logout process even if endpoint call fails
    }
    
    // Get client_login_base_url from sessionStorage before clearing auth data
    const clientLoginBaseUrl = sessionStorage.getItem('client_base_url');
    
    // Use provided clientId and instituteId, or fallback to sessionStorage
    const finalClientId = clientId || sessionStorage.getItem('client_id');
    const finalInstituteId = instituteId || sessionStorage.getItem('institute_id');
    
    // Always clear auth data
    this.clearAuthData();
    console.log('Logout successful');
    console.log('Logout - userRole:', userRole);
    console.log('Logout - clientId:', finalClientId);
    console.log('Logout - instituteId:', finalInstituteId);
    console.log('Logout - clientLoginBaseUrl:', clientLoginBaseUrl);
    
    // Determine redirect destination based on user role
    let redirectUrl = '/auth'; // Default fallback
    
    if (userRole) {
      // Handle both string and array formats for roles
      let role = userRole;
      let roleArray = [];
      
      // If userRole is an array, use it directly
      if (Array.isArray(userRole)) {
        roleArray = userRole;
        role = userRole.length > 0 ? userRole[0] : null;
      } else if (typeof userRole === 'string') {
        // If it's a string, check if it contains comma-separated roles
        roleArray = userRole.split(',').map(r => r.trim());
        [role] = roleArray;
      }
      
      // Ensure role is a string before calling toLowerCase
      if (role && typeof role === 'string') {
        role = role.toLowerCase();
        
        // Check if 'client' or 'admin' role exists (client and admin are used interchangeably)
        const adminRoles = ['client', 'admin', 'superadmin'];
        const hasClientOrAdminRole = roleArray.some(r => 
          typeof r === 'string' && adminRoles.includes(r.toLowerCase())
        ) || adminRoles.includes(role);
        
        // Check if 'student' role exists in the role array
        const hasStudentRole = roleArray.some(r => 
          typeof r === 'string' && r.toLowerCase() === 'student'
        ) || role === 'student';
        
        // Prioritize client/admin role for logout redirect - always redirect to /client-auth for client/admin users
        if (hasClientOrAdminRole) {
          console.log('Client/Admin role detected, redirecting to /client-auth');
          redirectUrl = '/client-auth';
        } else if (hasStudentRole) {
          console.log('Student role detected, redirecting to /student-auth');
          redirectUrl = '/student-auth';
        } else if (clientLoginBaseUrl) {
          // For other roles, check if client_login_base_url is available
          console.log('Using clientLoginBaseUrl:', clientLoginBaseUrl);
          redirectUrl = clientLoginBaseUrl;
        }
      } else if (clientLoginBaseUrl) {
        // If role is not a valid string, fallback to client_login_base_url
        redirectUrl = clientLoginBaseUrl;
      }
    } else if (clientLoginBaseUrl) {
      // No role provided, check for client_login_base_url first
      redirectUrl = clientLoginBaseUrl;
    }
    
    // Additional checks to ensure client users go to /client-auth
    // 1. If we have client_id but no institute_id, assume client user
    if (redirectUrl === '/auth' && finalClientId && !finalInstituteId) {
      console.log('Client ID found but no institute_id, redirecting to /client-auth');
      redirectUrl = '/client-auth';
    }
    
    // 2. If current URL path contains 'client', redirect to /client-auth
    if (redirectUrl === '/auth' && window.location.pathname.includes('client')) {
      console.log('Current path contains "client", redirecting to /client-auth');
      redirectUrl = '/client-auth';
    }
    
    // Append query parameters only for student-auth route (not for client-auth)
    if (redirectUrl === '/student-auth' && finalClientId && finalInstituteId) {
      const url = new URL(redirectUrl, window.location.origin);
      url.searchParams.set('client_id', finalClientId);
      url.searchParams.set('institute_id', finalInstituteId);
      redirectUrl = url.pathname + url.search;
    }
    
    console.log('Final redirect URL:', redirectUrl);
    window.location.href = redirectUrl;
  }

  /**
   * Refresh user session
   * @returns {Promise<Object>} - Updated user data
   */
  static async refreshSession() {
    try {
      const response = await axiosInstance.post(`${BASE_URL}v1/auth/refresh`);
      
      return response.data;
    } catch (error) {
      console.error('Session refresh failed:', error);
      throw error;
    }
  }

  /**
   * Generate random state for CSRF protection
   * @returns {string} - Random state string
   */
  static generateState() {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Generate random nonce for replay protection
   * @returns {string} - Random nonce string
   */
  static generateNonce() {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15) +
           Date.now().toString(36);
  }

  /**
   * Clear any stored authentication data
   */
  static clearAuthData() {
    // Clear all authentication data from session storage
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('token_type');
    sessionStorage.removeItem('token_expires_in');
    sessionStorage.removeItem('token_expiry');
    sessionStorage.removeItem('auth_state');
    sessionStorage.removeItem('auth_nonce');
    sessionStorage.removeItem('auth_redirect');
    // Note: client_base_url is NOT cleared here so it can be used for logout redirect
  }
}

export default OIDCAuthService; 